/**
 * SAP CDC (Customer Data Cloud) Aut      if (this.isDevelopment) {
        console.log('🔧 [SAP CDC] Initializing service...');
      }entication Service
 * Handles initialization, login, and token management for SAP CDC
 */

class SAPCDCService {
  constructor() {
    // Singleton pattern - prevent multiple instances
    if (SAPCDCService.instance) {
      return SAPCDCService.instance;
    }
    SAPCDCService.instance = this;
    
    this.isInitialized = false;
    this.isInitializing = false; // Prevent concurrent initializations
    this.gigya = null;
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isLocalHost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';
    
    // Check if domain is production environment
    this.isProductionDomain = window.location.hostname === 'adapt.laerdal.io';
    
    // Cache for authentication status (5 minutes)
    this.authCache = {
      isAuthenticated: null,
      userInfo: null,
      token: null,
      lastCheck: 0,
      userInfoLastCheck: 0,
      tokenLastCheck: 0,
      cacheDuration: 5 * 60 * 1000 // 5 minutes
    };
    
    // API Keys
    this.apiKeys = {
      development: '4_D0XjqWc0n16uInJitxKlPA',
      production: '4_xkg6CCoEZcObgNhYJ6jv0A'
    };
    
    this.currentApiKey = this.isDevelopment ? this.apiKeys.development : this.apiKeys.production;
    
    // Logging for both environments
    if (this.isDevelopment) {
      console.log('🚀 [SAP CDC] Initializing development service...');
      console.log('🏠 [SAP CDC] Localhost environment detected');
    } else {
      console.log('🚀 [SAP CDC] Initializing production service...');
      console.log('🌐 [SAP CDC] Production domain:', window.location.hostname);
      console.log('🔑 [SAP CDC] Using production API key');
    }
  }

  /**
   * Load SAP CDC SDK script dynamically
   */
  async loadSAPCDCScript() {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (window.gigya && window.gigya.isGigya) {
        if (this.isDevelopment) {
          console.log('✅ [SAP CDC] Script already loaded, skipping reload');
        }
        this.gigya = window.gigya;
        resolve(window.gigya);
        return;
      }

      // Check if script tag already exists to prevent double loading
      const existingScript = document.querySelector(`script[src*="gigya.js"][src*="${this.currentApiKey}"]`);
      if (existingScript) {
        if (this.isDevelopment) {
          console.log('⚠️ [SAP CDC] Script tag exists, waiting for load...');
        }
        
        // Wait for existing script to load
        const checkInterval = setInterval(() => {
          if (window.gigya && window.gigya.isGigya) {
            clearInterval(checkInterval);
            this.gigya = window.gigya;
            resolve(window.gigya);
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.gigya) {
            reject(new Error('Timeout waiting for existing SAP CDC script to load'));
          }
        }, 10000);
        
        return;
      }

      console.log('📥 [SAP CDC Service] Loading SAP CDC SDK script...');
      
      const script = document.createElement('script');
      script.src = 'https://cdns.gigya.com/js/gigya.js?apikey=' + this.currentApiKey;
      script.async = true;
      script.crossOrigin = 'anonymous'; // Add explicit CORS handling
      
      script.onload = () => {
        console.log('✅ [SAP CDC Service] SAP CDC SDK script loaded successfully');
        
        // Check for gigya methods that indicate proper loading
        if (window.gigya && window.gigya.isGigya) {
          console.log('🔧 [SAP CDC Service] Available Gigya methods:', 
            Object.keys(window.gigya).length > 0 ? 
            `(${Object.keys(window.gigya).length}) ${Object.keys(window.gigya)}` : 
            'None available');
        }
        
        this.gigya = window.gigya;
        resolve(window.gigya);
      };
      
      script.onerror = (error) => {
        console.error('❌ [SAP CDC Service] Failed to load SAP CDC SDK script:', error);
        
        // Provide specific guidance for domain issues
        if (!this.isDomainTrusted()) {
          console.error('💡 [SAP CDC Service] This error may be due to domain trust issues');
          this.showProductionDomainError();
        }
        
        reject(new Error('Failed to load SAP CDC SDK - possible domain trust issue'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize SAP CDC with proper configuration
   */
  async init() {
    // Prevent concurrent initializations
    if (this.isInitialized) {
      return true;
    }
    
    if (this.isInitializing) {
      // Wait for ongoing initialization
      await new Promise(resolve => {
        const checkInit = () => {
          if (this.isInitialized || !this.isInitializing) {
            resolve();
          } else {
            setTimeout(checkInit, 100);
          }
        };
        checkInit();
      });
      return this.isInitialized;
    }
    
    this.isInitializing = true;
    
    try {
      if (this.isDevelopment) {
        console.log('🚀 [SAP CDC] Starting initialization...');
      }
      
      // Check domain trust status
      if (!this.isDomainTrusted()) {
        console.warn('⚠️ [SAP CDC Service] Domain may not be trusted by SAP CDC');
        this.showProductionDomainError();
      }
      
      // Load the SDK script first
      await this.loadSAPCDCScript();
      
      if (!window.gigya || !window.gigya.services) {
        throw new Error('SAP CDC SDK not properly loaded');
      }

      if (this.isDevelopment) {
        console.log('🔧 [SAP CDC] Configuring...');
      }
      
      // Initialize with configuration
      const initConfig = {
        apiKey: this.currentApiKey,
        enabledProviders: '*',
        lang: 'en',
        ...(this.isLocalHost && {
          // Add localhost-specific configurations if needed
          cookiePath: '/',
          cookieDomain: 'localhost'
        })
      };

      console.log('📋 [SAP CDC Service] Init configuration:', {
        apiKey: initConfig.apiKey,
        enabledProviders: initConfig.enabledProviders,
        lang: initConfig.lang,
        isLocalHost: this.isLocalHost
      });

      // Call gigya init with enhanced error handling
      try {
        await this.callGigyaInit(initConfig);
        this.isInitialized = true;
        if (this.isDevelopment) {
          console.log('✅ [SAP CDC] Initialized successfully');
        }
      } catch (initError) {
        if (!this.isDomainTrusted()) {
          console.error('❌ [SAP CDC] Init failed due to domain trust issues');
          this.showProductionDomainError();
          throw new Error('SAP CDC initialization failed: Domain not trusted. Please contact administrator to add adapt.laerdal.io to trusted domains.');
        }
        throw initError;
      }
      
      // Check if user is already authenticated
      await this.checkAuthenticationStatus();
      
      return true;
    } catch (error) {
      console.error('❌ [SAP CDC] Initialization failed:', error);
      
      // Provide user-friendly error message
      if (error.message && error.message.includes('CORS')) {
        throw new Error('SAP CDC initialization failed due to domain configuration. Please contact your administrator.');
      }
      
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Call gigya.init with proper error handling
   */
  async callGigyaInit(config) {
    return new Promise((resolve, reject) => {
      try {
        // Check if gigya is available and has the correct structure
        if (!window.gigya) {
          throw new Error('Gigya SDK not loaded');
        }

        console.log('🔧 [SAP CDC Service] Available Gigya methods:', Object.keys(window.gigya));
        
        // Use the correct Gigya initialization method
        // Try gigya.services.socialize.addEventHandlers first (most common)
        if (window.gigya.services && window.gigya.services.socialize && typeof window.gigya.services.socialize.addEventHandlers === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.services.socialize.addEventHandlers() method');
          
          // Set API key directly
          window.gigya.apiKey = config.apiKey;
          
          // Add event handlers for proper initialization
          window.gigya.services.socialize.addEventHandlers({
            onLogin: function(response) {
              console.log('🔐 [SAP CDC Service] Login event detected via services API');
            }
          });
          
          setTimeout(() => {
            console.log('✅ [SAP CDC Service] Services API configuration completed');
            resolve({ errorCode: 0, message: 'Services API configuration successful' });
          }, 100);
          
        } else if (typeof window.gigya.init === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.init() method');
          window.gigya.init({
            ...config,
            callback: (response) => {
              if (response && response.errorCode === 0) {
                console.log('✅ [SAP CDC Service] Gigya init callback successful');
                resolve(response);
              } else {
                console.error('❌ [SAP CDC Service] Gigya init callback error:', response);
                reject(new Error(`Gigya init failed: ${response?.errorMessage || 'Unknown error'}`));
              }
            }
          });
        } else if (typeof window.gigya.socialize?.init === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.socialize.init() method');
          window.gigya.socialize.init({
            ...config,
            callback: (response) => {
              if (response && response.errorCode === 0) {
                console.log('✅ [SAP CDC Service] Gigya socialize init callback successful');
                resolve(response);
              } else {
                console.error('❌ [SAP CDC Service] Gigya socialize init callback error:', response);
                reject(new Error(`Gigya socialize init failed: ${response?.errorMessage || 'Unknown error'}`));
              }
            }
          });
        } else {
          // Fallback - try direct configuration
          console.log('⚠️ [SAP CDC Service] No standard init method found, using direct configuration');
          console.log('🔧 [SAP CDC Service] Setting gigya configuration directly');
          
          // Set the API key directly if no init method is available
          if (window.gigya.apiKey !== undefined) {
            window.gigya.apiKey = config.apiKey;
          }
          
          // Try to set additional configuration
          if (window.gigya.defaultEventMaps) {
            console.log('🔧 [SAP CDC Service] Default event maps available');
          }
          
          setTimeout(() => {
            console.log('✅ [SAP CDC Service] Direct configuration completed');
            resolve({ errorCode: 0, message: 'Direct configuration successful' });
          }, 100);
        }
      } catch (error) {
        console.error('❌ [SAP CDC Service] Exception during gigya init:', error);
        reject(error);
      }
    });
  }

  /**
   * Register a new user using SAP CDC REST API
   */
  async registerViaSAPCDCAPI(userDetails) {
    try {
      console.log('\n📝 [SAP CDC Service] Starting SAP CDC REST API registration...');
      console.log('📧 [SAP CDC Service] Email:', userDetails.email);
      
      const apiKey = this.getAPIKey();
      const registrationEndpoint = `https://accounts.gigya.com/accounts.register`;
      
      const formData = new FormData();
      formData.append('apiKey', apiKey);
      formData.append('email', userDetails.email);
      formData.append('password', userDetails.password);
      formData.append('profile.firstName', userDetails.firstName || userDetails.email.split('@')[0].split('.')[0]);
      formData.append('profile.lastName', userDetails.lastName || userDetails.email.split('@')[0].split('.')[1] || '');
      formData.append('profile.email', userDetails.email);
      formData.append('format', 'json');
      
      console.log('🌐 [SAP CDC Service] Making REST API call to:', registrationEndpoint);
      console.log('📋 [SAP CDC Service] Registration data:', {
        apiKey: apiKey,
        email: userDetails.email,
        hasPassword: !!userDetails.password,
        firstName: userDetails.firstName || userDetails.email.split('@')[0].split('.')[0],
        lastName: userDetails.lastName || userDetails.email.split('@')[0].split('.')[1] || ''
      });
      
      const response = await fetch(registrationEndpoint, {
        method: 'POST',
        body: formData,
        credentials: 'include' // Include cookies for proper domain handling
      });
      
      const result = await response.json();
      
      console.log('📥 [SAP CDC Service] Registration API response:', {
        errorCode: result.errorCode,
        errorMessage: result.errorMessage,
        statusCode: response.status,
        hasUID: !!result.UID
      });
      
      if (result.errorCode === 0) {
        console.log('✅ [SAP CDC Service] REST API registration successful!');
        console.log('👤 [SAP CDC Service] New user UID:', result.UID);
        
        return {
          success: true,
          UID: result.UID,
          user: result,
          response: result
        };
      } else {
        console.error('❌ [SAP CDC Service] REST API registration failed:', result.errorMessage);
        console.log('🔍 [SAP CDC Service] Registration error details:', {
          errorCode: result.errorCode,
          errorDetails: this.getErrorDetails(result.errorCode),
          suggestedAction: this.getSuggestedAction(result.errorCode)
        });
        
        throw new Error(result.errorMessage || 'Registration failed');
      }
      
    } catch (error) {
      console.error('❌ [SAP CDC Service] REST API registration error:', error);
      throw error;
    }
  }

  /**
   * Register a new user in SAP CDC (Legacy SDK method - kept for compatibility)
   */
  async register(userDetails) {
    try {
      console.log('\n📝 [SAP CDC Service] Starting SAP CDC user registration...');
      console.log('📧 [SAP CDC Service] Email:', userDetails.email);
      
      if (!this.isInitialized) {
        console.log('⚠️ [SAP CDC Service] Not initialized, initializing now...');
        await this.init();
      }

      const registrationParams = {
        email: userDetails.email,
        password: userDetails.password,
        profile: {
          firstName: userDetails.firstName || userDetails.email.split('@')[0].split('.')[0],
          lastName: userDetails.lastName || userDetails.email.split('@')[0].split('.')[1] || '',
          email: userDetails.email
        },
        callback: this.handleRegistrationResponse.bind(this)
      };

      console.log('📋 [SAP CDC Service] Registration params:', {
        email: registrationParams.email,
        hasPassword: !!registrationParams.password,
        firstName: registrationParams.profile.firstName,
        lastName: registrationParams.profile.lastName
      });

      return new Promise((resolve, reject) => {
        this.registrationResolve = resolve;
        this.registrationReject = reject;
        
        // Try different registration methods
        if (window.gigya && typeof window.gigya.register === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.register() method');
          window.gigya.register(registrationParams);
        } else if (window.gigya && window.gigya.accounts && typeof window.gigya.accounts.register === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.accounts.register() method');
          window.gigya.accounts.register(registrationParams);
        } else if (window.gigya && window.gigya.socialize && typeof window.gigya.socialize.addConnection === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.socialize.addConnection() method');
          window.gigya.socialize.addConnection(registrationParams);
        } else {
          console.error('❌ [SAP CDC Service] No suitable registration method found');
          console.log('🔍 [SAP CDC Service] Available gigya methods:', window.gigya ? Object.keys(window.gigya) : 'gigya not available');
          reject(new Error('No suitable Gigya registration method found'));
        }
      });
      
    } catch (error) {
      console.error('❌ [SAP CDC Service] Registration failed:', error);
      throw error;
    }
  }

  /**
   * Handle registration response from SAP CDC
   */
  handleRegistrationResponse(response) {
    console.log('📥 [SAP CDC Service] Registration response received');
    console.log('📋 [SAP CDC Service] Registration details:', {
      errorCode: response.errorCode,
      errorMessage: response.errorMessage,
      hasUser: !!response.user,
      callId: response.callId,
      UID: response.UID
    });

    if (response.errorCode === 0) {
      console.log('✅ [SAP CDC Service] Registration successful!');
      console.log('👤 [SAP CDC Service] New user details:', {
        UID: response.UID,
        email: response.user?.profile?.email,
        firstName: response.user?.profile?.firstName,
        lastName: response.user?.profile?.lastName,
        isVerified: response.user?.isVerified
      });
      
      if (this.registrationResolve) {
        this.registrationResolve({
          success: true,
          user: response.user,
          UID: response.UID,
          response: response
        });
      }
    } else {
      console.error('❌ [SAP CDC Service] Registration failed:', response.errorMessage);
      console.log('🔍 [SAP CDC Service] Registration error details:', {
        errorCode: response.errorCode,
        errorDetails: this.getErrorDetails(response.errorCode),
        suggestedAction: this.getSuggestedAction(response.errorCode)
      });
      
      if (this.registrationReject) {
        this.registrationReject(new Error(response.errorMessage || 'Registration failed'));
      }
    }
  }

  /**
   * Perform SAP CDC login
   */
  async login(options = {}) {
    try {
      console.log('\n🔐 [SAP CDC Service] Starting SAP CDC login...');
      
      if (!this.isInitialized) {
        console.log('⚠️ [SAP CDC Service] Not initialized, initializing now...');
        await this.init();
      }

      // Check if credentials are provided for direct login
      const hasCredentials = options.loginID && options.password;
      
      if (hasCredentials) {
        console.log('📝 [SAP CDC Service] Credentials provided, attempting direct login...');
        console.log('📧 [SAP CDC Service] Login ID:', options.loginID);
        return this.performCredentialLogin(options);
      } else {
        console.log('🖥️ [SAP CDC Service] No credentials provided, showing login UI...');
        return this.performUILogin(options);
      }
      
    } catch (error) {
      console.error('❌ [SAP CDC Service] Login failed:', error);
      throw error;
    }
  }

  /**
   * Perform credential-based login
   */
  async performCredentialLogin(credentials) {
    console.log('🔑 [SAP CDC Service] Performing credential-based login...');
    
    const loginParams = {
      loginID: credentials.loginID,
      password: credentials.password,
      callback: this.handleLoginResponse.bind(this)
    };

    return new Promise((resolve, reject) => {
      this.loginResolve = resolve;
      this.loginReject = reject;
      
      // Try different login methods for credential-based authentication
      if (window.gigya && typeof window.gigya.login === 'function') {
        console.log('✅ [SAP CDC Service] Using gigya.login() method');
        window.gigya.login(loginParams);
      } else if (window.gigya && window.gigya.accounts && typeof window.gigya.accounts.login === 'function') {
        console.log('✅ [SAP CDC Service] Using gigya.accounts.login() method');
        window.gigya.accounts.login(loginParams);
      } else if (window.gigya && window.gigya.socialize && typeof window.gigya.socialize.login === 'function') {
        console.log('✅ [SAP CDC Service] Using gigya.socialize.login() method');
        window.gigya.socialize.login(loginParams);
      } else {
        console.error('❌ [SAP CDC Service] No suitable credential login method found');
        console.log('🔍 [SAP CDC Service] Available gigya methods:', window.gigya ? Object.keys(window.gigya) : 'gigya not available');
        reject(new Error('No suitable Gigya credential login method found'));
      }
    });
  }

  /**
   * Perform UI-based login
   */
  async performUILogin(options = {}) {
    console.log('🖥️ [SAP CDC Service] Performing UI-based login...');
    
    const loginOptions = {
      callback: this.handleLoginResponse.bind(this),
      ...options
    };

    console.log('📋 [SAP CDC Service] Login options:', {
      hasCallback: !!loginOptions.callback,
      additionalOptions: Object.keys(options)
    });

    return new Promise((resolve, reject) => {
      this.loginResolve = resolve;
      this.loginReject = reject;
      
      if (this.isLocalHost) {
        console.log('🏠 [SAP CDC Service] LocalHost detected - Special handling required');
        console.log('💡 [SAP CDC Service] Note: For localhost, you may need to copy cookies from https://connect-dev.laerdal.com');
      }
      
      // Check available login methods and use the correct one
      if (window.gigya && typeof window.gigya.showLoginUI === 'function') {
        console.log('✅ [SAP CDC Service] Using gigya.showLoginUI() method');
        window.gigya.showLoginUI(loginOptions);
      } else if (window.gigya && window.gigya.socialize && typeof window.gigya.socialize.showLoginUI === 'function') {
        console.log('✅ [SAP CDC Service] Using gigya.socialize.showLoginUI() method');
        window.gigya.socialize.showLoginUI(loginOptions);
      } else if (window.gigya && window.gigya.services && window.gigya.services.socialize && typeof window.gigya.services.socialize.login === 'function') {
        console.log('✅ [SAP CDC Service] Using gigya.services.socialize.login() method');
        window.gigya.services.socialize.login(loginOptions);
      } else {
        console.error('❌ [SAP CDC Service] No suitable login method found');
        console.log('🔍 [SAP CDC Service] Available gigya methods:', window.gigya ? Object.keys(window.gigya) : 'gigya not available');
        reject(new Error('No suitable Gigya login method found'));
      }
    });
  }

  /**
   * Handle login response from SAP CDC
   */
  handleLoginResponse(response) {
    console.log('📥 [SAP CDC Service] Login response received');
    console.log('📋 [SAP CDC Service] Response details:', {
      errorCode: response.errorCode,
      errorMessage: response.errorMessage,
      hasUser: !!response.user,
      callId: response.callId
    });

    if (response.errorCode === 0) {
      console.log('✅ [SAP CDC Service] Login successful!');
      console.log('👤 [SAP CDC Service] User details:', {
        isLoggedIn: response.user?.isLoggedIn,
        UID: response.user?.UID,
        loginProvider: response.user?.loginProvider,
        email: response.user?.profile?.email,
        firstName: response.user?.profile?.firstName,
        lastName: response.user?.profile?.lastName
      });
      
      this.currentUser = response.user;
      
      if (this.loginResolve) {
        this.loginResolve({
          success: true,
          user: response.user,
          token: {
            sessionToken: response.sessionInfo?.sessionToken,
            sessionSecret: response.sessionInfo?.sessionSecret,
            sessionExpiration: response.sessionInfo?.sessionExpiration
          },
          response: response
        });
      }
    } else {
      console.error('❌ [SAP CDC Service] Login failed:', response.errorMessage);
      console.log('🔍 [SAP CDC Service] Error details:', {
        errorCode: response.errorCode,
        errorDetails: this.getErrorDetails(response.errorCode),
        suggestedAction: this.getSuggestedAction(response.errorCode)
      });
      
      // Special handling for Invalid LoginID in new production instance
      if (response.errorCode === 403042) {
        console.log('💡 [SAP CDC Service] This appears to be a new production instance.');
        console.log('📝 [SAP CDC Service] Users from the previous instance need to register again.');
        console.log('🔗 [SAP CDC Service] Please use the "Sign Up" option to create your account.');
      }
      
      if (this.loginReject) {
        this.loginReject(new Error(response.errorMessage || 'Login failed'));
      }
    }
  }

  /**
   * Initiate forgot password flow
   * Sends password reset email to user
   */
  async forgotPassword(email) {
    try {
      console.log('\n🔐 [SAP CDC Service] Starting forgot password flow...');
      console.log('📧 [SAP CDC Service] Email:', email);
      
      if (!this.isInitialized) {
        console.log('⚠️ [SAP CDC Service] Not initialized, initializing now...');
        await this.init();
      }

      const forgotPasswordParams = {
        loginID: email,
        callback: this.handleForgotPasswordResponse.bind(this)
      };

      console.log('📋 [SAP CDC Service] Forgot password params:', {
        loginID: email,
        hasCallback: !!forgotPasswordParams.callback
      });

      return new Promise((resolve, reject) => {
        this.forgotPasswordResolve = resolve;
        this.forgotPasswordReject = reject;
        
        // Try different forgot password methods
        if (window.gigya && window.gigya.accounts && typeof window.gigya.accounts.resetPassword === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.accounts.resetPassword() method');
          window.gigya.accounts.resetPassword(forgotPasswordParams);
        } else if (window.gigya && typeof window.gigya.resetPassword === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.resetPassword() method');
          window.gigya.resetPassword(forgotPasswordParams);
        } else {
          console.error('❌ [SAP CDC Service] No suitable forgot password method found');
          console.log('🔍 [SAP CDC Service] Available gigya methods:', window.gigya ? Object.keys(window.gigya) : 'gigya not available');
          reject(new Error('No suitable Gigya forgot password method found'));
        }
      });
      
    } catch (error) {
      console.error('❌ [SAP CDC Service] Forgot password failed:', error);
      throw error;
    }
  }

  /**
   * Handle forgot password response from SAP CDC
   */
  handleForgotPasswordResponse(response) {
    console.log('📥 [SAP CDC Service] Forgot password response received');
    console.log('📋 [SAP CDC Service] Response details:', {
      errorCode: response.errorCode,
      errorMessage: response.errorMessage,
      callId: response.callId
    });

    if (response.errorCode === 0) {
      console.log('✅ [SAP CDC Service] Password reset email sent successfully!');
      
      if (this.forgotPasswordResolve) {
        this.forgotPasswordResolve({
          success: true,
          message: 'Password reset email sent successfully',
          response: response
        });
      }
    } else {
      console.error('❌ [SAP CDC Service] Forgot password failed:', response.errorMessage);
      console.log('🔍 [SAP CDC Service] Forgot password error details:', {
        errorCode: response.errorCode,
        errorDetails: this.getErrorDetails(response.errorCode),
        suggestedAction: this.getSuggestedAction(response.errorCode)
      });
      
      if (this.forgotPasswordReject) {
        this.forgotPasswordReject(new Error(response.errorMessage || 'Password reset failed'));
      }
    }
  }

  /**
   * Reset password using reset code and new password
   * Used when user clicks reset link in email
   */
  async resetPassword(resetToken, newPassword) {
    try {
      console.log('\n🔄 [SAP CDC Service] Starting password reset...');
      console.log('🎟️ [SAP CDC Service] Reset token provided:', !!resetToken);
      
      if (!this.isInitialized) {
        console.log('⚠️ [SAP CDC Service] Not initialized, initializing now...');
        await this.init();
      }

      const resetPasswordParams = {
        pwResetToken: resetToken,
        newPassword: newPassword,
        callback: this.handleResetPasswordResponse.bind(this)
      };

      console.log('📋 [SAP CDC Service] Reset password params:', {
        hasPwResetToken: !!resetPasswordParams.pwResetToken,
        hasNewPassword: !!resetPasswordParams.newPassword,
        hasCallback: !!resetPasswordParams.callback
      });

      return new Promise((resolve, reject) => {
        this.resetPasswordResolve = resolve;
        this.resetPasswordReject = reject;
        
        // Try different reset password methods
        if (window.gigya && window.gigya.accounts && typeof window.gigya.accounts.resetPassword === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.accounts.resetPassword() method');
          window.gigya.accounts.resetPassword(resetPasswordParams);
        } else if (window.gigya && typeof window.gigya.resetPassword === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.resetPassword() method');
          window.gigya.resetPassword(resetPasswordParams);
        } else {
          console.error('❌ [SAP CDC Service] No suitable reset password method found');
          console.log('🔍 [SAP CDC Service] Available gigya methods:', window.gigya ? Object.keys(window.gigya) : 'gigya not available');
          reject(new Error('No suitable Gigya reset password method found'));
        }
      });
      
    } catch (error) {
      console.error('❌ [SAP CDC Service] Reset password failed:', error);
      throw error;
    }
  }

  /**
   * Handle reset password response from SAP CDC
   */
  handleResetPasswordResponse(response) {
    console.log('📥 [SAP CDC Service] Reset password response received');
    console.log('📋 [SAP CDC Service] Response details:', {
      errorCode: response.errorCode,
      errorMessage: response.errorMessage,
      callId: response.callId
    });

    if (response.errorCode === 0) {
      console.log('✅ [SAP CDC Service] Password reset successful!');
      
      if (this.resetPasswordResolve) {
        this.resetPasswordResolve({
          success: true,
          message: 'Password reset successful',
          response: response
        });
      }
    } else {
      console.error('❌ [SAP CDC Service] Password reset failed:', response.errorMessage);
      console.log('🔍 [SAP CDC Service] Reset password error details:', {
        errorCode: response.errorCode,
        errorDetails: this.getErrorDetails(response.errorCode),
        suggestedAction: this.getSuggestedAction(response.errorCode)
      });
      
      if (this.resetPasswordReject) {
        this.resetPasswordReject(new Error(response.errorMessage || 'Password reset failed'));
      }
    }
  }

  /**
   * Get detailed error information
   */
  getErrorDetails(errorCode) {
    const errorMap = {
      403042: 'Invalid LoginID - User not found in SAP CDC system',
      403041: 'Invalid Password - Incorrect password provided',
      403043: 'Invalid Email - Email format is incorrect',
      403044: 'Account Disabled - User account has been disabled',
      403045: 'Account Pending Registration - Account exists but not verified',
      400006: 'Missing Required Fields - LoginID or Password not provided',
      400009: 'Invalid Parameters - Request parameters are malformed',
      403047: 'Invalid Reset Token - Password reset token is invalid or expired',
      403048: 'Password Reset Token Expired - Reset token has expired',
      400110: 'Password Policy Violation - New password does not meet requirements'
    };
    
    return errorMap[errorCode] || `Unknown error code: ${errorCode}`;
  }

  /**
   * Clear authentication cache
   */
  clearAuthCache() {
    this.authCache.isAuthenticated = null;
    this.authCache.userInfo = null;
    this.authCache.token = null;
    this.authCache.lastCheck = 0;
    this.authCache.userInfoLastCheck = 0;
    this.authCache.tokenLastCheck = 0;
  }

  /**
   * Get suggested action based on error code
   */
  getSuggestedAction(errorCode) {
    const actionMap = {
      403042: 'User needs to be registered in the new production instance. Please use the Sign Up option to create an account, or contact your administrator.',
      403041: 'Please check your password and try again.',
      403043: 'Please check the email format and try again.',
      403044: 'Contact your administrator to reactivate the account.',
      403045: 'Check your email for verification link or contact administrator.',
      400006: 'Ensure both email and password are provided.',
      400009: 'Check the login request format and try again.',
      403047: 'The password reset link is invalid or has expired. Please request a new password reset.',
      403048: 'The password reset link has expired. Please request a new password reset.',
      400110: 'Password must meet security requirements. Please choose a stronger password with at least 8 characters, including uppercase, lowercase, numbers, and special characters.'
    };
    
    return actionMap[errorCode] || 'Contact your administrator for assistance.';
  }

  /**
   * Check if user is currently authenticated
   */
  async isAuthenticated() {
    try {
      if (!this.isInitialized) {
        return false;
      }

      // Check cache first to avoid redundant calls
      const now = Date.now();
      if (this.authCache.lastCheck && 
          (now - this.authCache.lastCheck) < this.authCache.cacheDuration &&
          this.authCache.isAuthenticated !== null) {
        return this.authCache.isAuthenticated;
      }

      // Only log in development mode to reduce noise
      if (this.isDevelopment) {
        console.log('🔍 [SAP CDC] Checking auth status...');
      }
      
      return new Promise((resolve) => {
        // Try different methods to check authentication
        if (window.gigya && typeof window.gigya.getSession === 'function') {
          if (this.isDevelopment) {
            console.log('✅ [SAP CDC] Using gigya.getSession() method');
          }
          window.gigya.getSession({
            callback: (response) => {
              const isAuth = response.errorCode === 0 && response.sessionInfo?.loginToken;
              
              // Cache the result
              this.authCache.isAuthenticated = isAuth;
              this.authCache.lastCheck = now;
              
              if (this.isDevelopment) {
                console.log('📊 [SAP CDC] Auth check result:', {
                  errorCode: response.errorCode,
                  isAuthenticated: isAuth
                });
              }
              resolve(isAuth);
            }
          });
        } else if (window.gigya && window.gigya.socialize && typeof window.gigya.socialize.getUserInfo === 'function') {
          if (this.isDevelopment) {
            console.log('✅ [SAP CDC] Using gigya.socialize.getUserInfo() method');
          }
          window.gigya.socialize.getUserInfo({
            callback: (response) => {
              const isAuth = response.errorCode === 0 && response.user?.isLoggedIn;
              
              // Cache the result
              this.authCache.isAuthenticated = isAuth;
              this.authCache.lastCheck = now;
              
              if (this.isDevelopment) {
                console.log('📊 [SAP CDC] Auth check result:', {
                  errorCode: response.errorCode,
                  isAuthenticated: isAuth
                });
              }
              resolve(isAuth);
            }
          });
        } else if (window.gigya && window.gigya.services && window.gigya.services.socialize && typeof window.gigya.services.socialize.getUserInfo === 'function') {
          if (this.isDevelopment) {
            console.log('✅ [SAP CDC] Using gigya.services.socialize.getUserInfo() method');
          }
          window.gigya.services.socialize.getUserInfo({
            callback: (response) => {
              const isAuth = response.errorCode === 0 && response.user?.isLoggedIn;
              
              // Cache the result
              this.authCache.isAuthenticated = isAuth;
              this.authCache.lastCheck = now;
              
              if (this.isDevelopment) {
                console.log('📊 [SAP CDC] Auth check result:', {
                  errorCode: response.errorCode,
                  isAuthenticated: isAuth
                });
              }
              resolve(isAuth);
            }
          });
        } else {
          // Cache false result
          this.authCache.isAuthenticated = false;
          this.authCache.lastCheck = now;
          
          if (this.isDevelopment) {
            console.log('❌ [SAP CDC] No suitable auth method found');
          }
          resolve(false);
        }
      });
    } catch (error) {
      // Cache false result on error
      this.authCache.isAuthenticated = false;
      this.authCache.lastCheck = Date.now();
      
      if (this.isDevelopment) {
        console.error('❌ [SAP CDC] Auth check error:', error);
      }
      return false;
    }
  }

  /**
   * Get current user's access token
   */
  async getToken() {
    try {
      console.log('🎟️ [SAP CDC Service] Retrieving access token...');
      
      if (!this.isInitialized) {
        throw new Error('SAP CDC not initialized');
      }

      return new Promise((resolve, reject) => {
        // Try different methods to get token/session info
        if (window.gigya && typeof window.gigya.getSession === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.getSession() method');
          window.gigya.getSession({
            callback: (response) => {
              if (response.errorCode === 0) {
                console.log('✅ [SAP CDC Service] Session retrieved successfully');
                console.log('🔑 [SAP CDC Service] Session details:', {
                  hasLoginToken: !!response.sessionInfo?.loginToken,
                  hasSessionToken: !!response.sessionInfo?.sessionToken,
                  sessionExpiration: response.sessionInfo?.sessionExpiration
                });
                
                resolve({
                  loginToken: response.sessionInfo?.loginToken,
                  sessionToken: response.sessionInfo?.sessionToken,
                  sessionExpiration: response.sessionInfo?.sessionExpiration,
                  sessionInfo: response.sessionInfo
                });
              } else {
                console.error('❌ [SAP CDC Service] Failed to get session:', response.errorMessage);
                reject(new Error(response.errorMessage || 'Failed to get session'));
              }
            }
          });
        } else if (window.gigya && window.gigya.socialize && typeof window.gigya.socialize.getUserInfo === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.socialize.getUserInfo() method');
          window.gigya.socialize.getUserInfo({
            callback: (response) => {
              if (response.errorCode === 0) {
                console.log('✅ [SAP CDC Service] Token retrieved successfully');
                console.log('🔑 [SAP CDC Service] Token details:', {
                  hasSessionToken: !!response.sessionInfo?.sessionToken,
                  hasSessionSecret: !!response.sessionInfo?.sessionSecret,
                  sessionExpiration: response.sessionInfo?.sessionExpiration
                });
                
                resolve({
                  sessionToken: response.sessionInfo?.sessionToken,
                  sessionSecret: response.sessionInfo?.sessionSecret,
                  sessionExpiration: response.sessionInfo?.sessionExpiration,
                  user: response.user
                });
              } else {
                console.error('❌ [SAP CDC Service] Failed to get token:', response.errorMessage);
                reject(new Error(response.errorMessage || 'Failed to get token'));
              }
            }
          });
        } else if (window.gigya && window.gigya.services && window.gigya.services.socialize && typeof window.gigya.services.socialize.getUserInfo === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.services.socialize.getUserInfo() method');
          window.gigya.services.socialize.getUserInfo({
            callback: (response) => {
              if (response.errorCode === 0) {
                console.log('✅ [SAP CDC Service] Token retrieved successfully');
                console.log('🔑 [SAP CDC Service] Token details:', {
                  hasSessionToken: !!response.sessionInfo?.sessionToken,
                  hasSessionSecret: !!response.sessionInfo?.sessionSecret,
                  sessionExpiration: response.sessionInfo?.sessionExpiration
                });
                
                resolve({
                  sessionToken: response.sessionInfo?.sessionToken,
                  sessionSecret: response.sessionInfo?.sessionSecret,
                  sessionExpiration: response.sessionInfo?.sessionExpiration,
                  user: response.user
                });
              } else {
                console.error('❌ [SAP CDC Service] Failed to get token:', response.errorMessage);
                reject(new Error(response.errorMessage || 'Failed to get token'));
              }
            }
          });
        } else {
          console.error('❌ [SAP CDC Service] No suitable method found for getting token');
          reject(new Error('No suitable method found for getting token'));
        }
      });
    } catch (error) {
      console.error('❌ [SAP CDC Service] Error getting token:', error);
      throw error;
    }
  }

  /**
   * Get current user information
   */
  async getUserInfo() {
    try {
      // Check cache first to avoid redundant calls
      const now = Date.now();
      if (this.authCache.userInfo && 
          (now - this.authCache.userInfoLastCheck) < this.authCache.cacheDuration) {
        if (this.isDevelopment) {
          console.log('� [SAP CDC] Using cached user info');
        }
        return this.authCache.userInfo;
      }

      if (this.isDevelopment) {
        console.log('👤 [SAP CDC] Getting user information...');
      }
      
      if (!this.isInitialized) {
        throw new Error('SAP CDC not initialized');
      }

      return new Promise((resolve, reject) => {
        window.gigya.services.socialize.getUserInfo({
          callback: (response) => {
            if (response.errorCode === 0) {
              // Cache the user info
              this.authCache.userInfo = response.user;
              this.authCache.userInfoLastCheck = now;
              
              if (this.isDevelopment) {
                console.log('✅ [SAP CDC] User info retrieved successfully');
                console.log('📋 [SAP CDC] User details:', {
                  UID: response.user?.UID,
                  email: response.user?.profile?.email,
                  firstName: response.user?.profile?.firstName,
                  lastName: response.user?.profile?.lastName,
                  loginProvider: response.user?.loginProvider
                });
              }
              
              resolve(response.user);
            } else {
              if (this.isDevelopment) {
                console.error('❌ [SAP CDC] Failed to get user info:', response.errorMessage);
              }
              reject(new Error(response.errorMessage || 'Failed to get user info'));
            }
          }
        });
      });
    } catch (error) {
      console.error('❌ [SAP CDC Service] Error getting user info:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      console.log('🚪 [SAP CDC Service] Logging out user...');
      
      if (!this.isInitialized) {
        console.warn('⚠️ [SAP CDC Service] Not initialized, clearing local state only');
        this.currentUser = null;
        // Clear authentication cache
        this.clearAuthCache();
        return { success: true, message: 'Logged out (local only)' };
      }

      return new Promise((resolve) => {
        // Try different logout methods
        if (window.gigya && typeof window.gigya.logout === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.logout() method');
          window.gigya.logout({
            callback: (response) => {
              console.log('✅ [SAP CDC Service] Logout completed');
              console.log('📋 [SAP CDC Service] Logout response:', {
                errorCode: response.errorCode,
                errorMessage: response.errorMessage
              });
              this.currentUser = null;
              this.clearAuthCache();
              resolve(response);
            }
          });
        } else if (window.gigya && window.gigya.socialize && typeof window.gigya.socialize.logout === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.socialize.logout() method');
          window.gigya.socialize.logout({
            callback: (response) => {
              console.log('✅ [SAP CDC Service] Logout completed');
              console.log('📋 [SAP CDC Service] Logout response:', {
                errorCode: response.errorCode,
                errorMessage: response.errorMessage
              });
              this.currentUser = null;
              resolve(response);
            }
          });
        } else if (window.gigya && window.gigya.services && window.gigya.services.socialize && typeof window.gigya.services.socialize.logout === 'function') {
          console.log('✅ [SAP CDC Service] Using gigya.services.socialize.logout() method');
          window.gigya.services.socialize.logout({
            callback: (response) => {
              console.log('✅ [SAP CDC Service] Logout completed');
              console.log('📋 [SAP CDC Service] Logout response:', {
                errorCode: response.errorCode,
                errorMessage: response.errorMessage
              });
              this.currentUser = null;
              resolve(response);
            }
          });
        } else {
          console.warn('⚠️ [SAP CDC Service] No logout method found, clearing local state only');
          this.currentUser = null;
          resolve({ success: true, message: 'Logged out (local only)' });
        }
      });
    } catch (error) {
      console.error('❌ [SAP CDC Service] Error during logout:', error);
      this.currentUser = null; // Clear local state anyway
      throw error;
    }
  }

  /**
   * Check authentication status on initialization
   */
  async checkAuthenticationStatus() {
    try {
      const isAuth = await this.isAuthenticated();
      if (isAuth) {
        // Silent check - only log in development if needed
        const userInfo = await this.getUserInfo();
        return userInfo;
      } else {
        return null;
      }
    } catch (error) {
      if (this.isDevelopment) {
        console.error('❌ [SAP CDC] Auth status check failed:', error);
      }
      return null;
    }
  }

  /**
   * Helper method to handle localhost development
   */
  showLocalhostHelper() {
    if (this.isLocalHost) {
      console.log('\n🏠 [SAP CDC Service] LOCALHOST DEVELOPMENT HELPER');
      console.log('=============================================');
      console.log('IMPORTANT: For localhost to work with SAP CDC, follow these steps:');
      console.log('');
      console.log('1. 🌐 Open https://connect-dev.laerdal.com in a new tab');
      console.log('2. ⏳ Wait for it to redirect to id.laerdal.com');
      console.log('3. 🔍 Open Developer Tools (F12) → Network tab');
      console.log('4. 🍪 Check that cookies are saved from id.laerdal.com');
      console.log('5. 🏠 Return to localhost and click "Login with SAP CDC"');
      console.log('');
      console.log('💡 Why this works:');
      console.log('   - SAP CDC requires SSL certificates for authentication');
      console.log('   - Localhost cannot provision web certificates');
      console.log('   - By visiting connect-dev.laerdal.com → id.laerdal.com first,');
      console.log('     you establish the necessary authentication cookies');
      console.log('   - These cookies are then available for SAP CDC on localhost');
      console.log('');
      console.log('🔧 Alternative: Use a staging environment with proper SSL');
      console.log('=============================================\n');
    }
  }



  /**
   * Helper method to handle production domain errors
   */
  showProductionDomainError() {
    if (this.isProductionDomain) {
      console.error('\n🚨 [SAP CDC Service] PRODUCTION DOMAIN ERROR');
      console.error('===============================================');
      console.error('ISSUE: adapt.laerdal.io is not in SAP CDC trusted domains');
      console.error('');
      console.error('SOLUTION REQUIRED:');
      console.error('1. 🔧 Contact SAP CDC administrator to add domain');
      console.error('2. 📋 Add "adapt.laerdal.io" to trusted domains list');
      console.error('3. 🔄 This may also require "*laerdal.io" wildcard entry');
      console.error('');
      console.error('CURRENT TRUSTED DOMAINS INCLUDE:');
      console.error('- adapt.laerdal.com');
      console.error('- *.laerdal.com');
      console.error('- id.laerdal.com');
      console.error('');
      console.error('TEMPORARY WORKAROUND:');
      console.error('- Use adapt.laerdal.com subdomain if available');
      console.error('- Or update DNS to point to adapt.laerdal.com');
      console.error('===============================================\n');
    }
  }

  /**
   * Check if current domain has known SAP CDC issues
   */
  isDomainTrusted() {
    const hostname = window.location.hostname;
    
    // Known trusted domains
    const trustedDomains = [
      'localhost',
      '127.0.0.1',
      'adapt.laerdal.com',
      'id.laerdal.com',
      'connect-dev.laerdal.com',
      'adapt.laerdal.io'  // Production domain
    ];
    
    const trustedPatterns = [
      /.*\.laerdal\.com$/,
      /.*\.simcapture\.com$/,
      /.*\.laerdal\.io$/  // Support all subdomains of laerdal.io
    ];
    
    return trustedDomains.includes(hostname) || 
           trustedPatterns.some(pattern => pattern.test(hostname));
  }
}

// Export a singleton instance
export const sapCDCService = new SAPCDCService();
export default SAPCDCService;
